import { Form, Input, Modal } from "antd";
import { useState } from "react";
import { AppButton } from "components/button/AppButton";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { useQueryClient } from "react-query";
import { IdentifierProps } from "types";
import { generalValidationRules } from "utils/formHelpers/validations";
import { openNotification } from "utils/notification";
import { useCreateContextTip } from "../hooks/useCreateContextTip";
import { QUERY_KEY_CONTEXT_TIPS } from "../hooks/useGetContextTips";
import { IconDropzone } from "./IconDropzone";

export const AddContextTip = ({ open, handleClose }: IdentifierProps) => {
  const [form] = Form.useForm();
  const { token } = useGetUserInfo();
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useCreateContextTip();
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [iconError, setIconError] = useState<string | undefined>();

  const handleSubmit = (values: { title: string; subtitle?: string }) => {
    if (!iconFile) {
      setIconError("Icon image is required.");
      return;
    }
    setIconError(undefined);

    mutate(
      {
        token,
        title: values.title,
        subtitle: values.subtitle,
        icon: iconFile,
      },
      {
        onError: (err: any) => {
          const res = err.response?.data;
          let description = "An error occurred, please try again.";
          if (res?.message && typeof res.message === "string") {
            description = res.message;
          }
          if (res?.errors && typeof res.errors === "object") {
            const first = Object.values(res.errors)[0];
            if (Array.isArray(first) && first[0]) {
              description = String(first[0]);
            }
          }
          openNotification({
            title: "Error",
            state: "error",
            description,
            duration: 6,
          });
        },
        onSuccess: () => {
          openNotification({
            title: "Success",
            state: "success",
            description: "Context tip created successfully",
            duration: 4.5,
          });
          form.resetFields();
          setIconFile(null);
          queryClient.invalidateQueries({ queryKey: [QUERY_KEY_CONTEXT_TIPS] });
          handleClose();
        },
      },
    );
  };

  return (
    <Modal
      title="Create context tip"
      open={open}
      onCancel={() => handleClose()}
      footer={null}
      width="36rem"
      style={{ top: 20 }}
      destroyOnClose
    >
      <Form
        layout="vertical"
        requiredMark={false}
        form={form}
        onFinish={handleSubmit}
      >
        <Form.Item name="title" label="Title" rules={generalValidationRules}>
          <Input placeholder="Complete your profile" />
        </Form.Item>
        <Form.Item name="subtitle" label="Subtitle">
          <Input.TextArea
            rows={2}
            placeholder="Add skills and experience to improve visibility"
          />
        </Form.Item>
        <Form.Item label="Icon" required>
          <IconDropzone
            value={iconFile}
            onChange={(file) => {
              setIconFile(file);
              if (file) setIconError(undefined);
            }}
            error={iconError}
          />
        </Form.Item>
        <div className="flex justify-end gap-2 mt-4">
          <AppButton
            label="Cancel"
            variant="transparent"
            handleClick={() => handleClose()}
          />
          <AppButton label="Create" isLoading={isLoading} type="submit" />
        </div>
      </Form>
    </Modal>
  );
};
